import { ReloadOutlined } from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import { Button } from '@surecompanies/core_components'
import { Alert, Button as AntButton, Select, Space, Table, Tag } from 'antd'
import styled from 'styled-components'
import type { ColumnsType } from 'antd/es/table'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import {
  BENEFIT_INTEGRATIONS,
  noyoIssuesPath,
} from 'enrollment-platform/pages/BenefitIntegrations/paths'
import type {
  NoyoDifference,
  NoyoGroupPlan,
  NoyoPlanMember,
} from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.types'
import { getNoyoPlanMembers } from 'enrollment-platform/api/partner-benefit-graph/noyo-differences.client'
import Page from 'layouts/Page'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FieldsComparison } from './FieldsComparison'
import { MemberNameCell } from './MemberNameCell'
import { NoyoIssueExpandIcon } from './NoyoIssueExpandIcon'
import { NoyoPlanCell, fetchNoyoGroupPlan } from './NoyoPlanCell'
import { SureCoPlanCell } from './SureCoPlanCell'
import { noyoTrackingUrl } from './issueLinks'
import { differenceTypeLabel } from './differenceTypeLabel'
import { formatUnixTime, impactColor, impactLabel } from './impactLabel'
import { useNoyoIssues } from './hooks/useNoyoIssues'
import { planIdFromFields } from './memberName'
import { planDateMismatches } from './noyoPlanLabel'
import {
  bothEmployeesTerminated,
  surecoActiveWhileNoyoTerminated,
} from './surecoActiveWhileNoyoTerminated'
import {
  DEFAULT_NOYO_ISSUE_QUEUES,
  filterIssuesByQueue,
  isNoyoClassification,
  isNoyoIssueQueue,
  issueQueueLabel,
  NOYO_CLASSIFICATION_OPTIONS,
  NOYO_ISSUE_QUEUE_OPTIONS,
  problemTagColor,
} from './noyoIssueFilters'

const benefitIntegrationsListPath = () => `/${BENEFIT_INTEGRATIONS}`

const CenteredActionAlert = styled.div`
  .ant-alert-action {
    align-self: center;
  }
`

const TableWrap = styled.div`
  tr.noyo-issue-row-noyo-terminated > td {
    background: #fff1f0 !important;
  }
  tr.noyo-issue-row-noyo-terminated:hover > td {
    background: #ffccc7 !important;
  }
  tr.noyo-issue-row-noyo-terminated > td:first-child {
    box-shadow: inset 4px 0 0 #cf1322;
  }
`

type PlanMembersState = {
  loading: boolean
  surecoMembers?: NoyoPlanMember[]
  noyoMembers?: NoyoPlanMember[]
  surecoPlan?: NoyoGroupPlan
  noyoPlan?: NoyoGroupPlan
}

export default function NoyoIssues() {
  const {
    issues,
    meta,
    page,
    pageSize,
    classifications,
    loading,
    loadError,
    setPage,
    setPageSize,
    setClassifications,
    refresh,
  } = useNoyoIssues()
  const [planMembersById, setPlanMembersById] = useState<
    Record<string, PlanMembersState>
  >({})
  const [selectedQueues, setSelectedQueues] = useState(() => [
    ...DEFAULT_NOYO_ISSUE_QUEUES,
  ])
  const planMembersInFlight = useRef(new Set<string>())
  const planMembersFetchGen = useRef(0)
  const queuedIssues = useMemo(
    () =>
      issues.map((row) => {
        const planMembers = planMembersById[row.id]
        if (
          planMembers?.loading !== true &&
          bothEmployeesTerminated(
            planMembers?.surecoMembers,
            planMembers?.noyoMembers
          )
        ) {
          return { ...row, issueQueue: 'noise' as const }
        }
        return row
      }),
    [issues, planMembersById]
  )
  const visibleIssues = useMemo(
    () => filterIssuesByQueue(queuedIssues, selectedQueues),
    [queuedIssues, selectedQueues]
  )

  const loadPlanMembers = useCallback(
    async (row: NoyoDifference, gen: number) => {
      if (row.employee_id.length === 0) {
        return
      }
      if (planMembersInFlight.current.has(row.id)) {
        return
      }
      planMembersInFlight.current.add(row.id)
      setPlanMembersById((current) => ({
        ...current,
        [row.id]: { loading: true },
      }))

      try {
        const planId = planIdFromFields(row.fields)
        const lineOfCoverage =
          typeof row.lineOfCoverage === 'string' ? row.lineOfCoverage : ''
        const noyoPlanId =
          typeof planId === 'string' && planId.length > 0 ? planId : undefined
        const [result, noyoPlan] = await Promise.all([
          getNoyoPlanMembers(
            row.employee_id,
            noyoPlanId,
            lineOfCoverage.length > 0 ? lineOfCoverage : undefined
          ),
          noyoPlanId != null
            ? fetchNoyoGroupPlan(noyoPlanId).catch(() => undefined)
            : Promise.resolve(undefined),
        ])
        if (gen !== planMembersFetchGen.current) {
          return
        }
        setPlanMembersById((current) => ({
          ...current,
          [row.id]: {
            loading: false,
            surecoMembers: result.surecoMembers,
            noyoMembers: result.noyoMembers,
            surecoPlan: result.surecoPlan,
            noyoPlan,
          },
        }))
      } catch {
        if (gen !== planMembersFetchGen.current) {
          return
        }
        setPlanMembersById((current) => ({
          ...current,
          [row.id]: { loading: false },
        }))
      } finally {
        if (gen === planMembersFetchGen.current) {
          planMembersInFlight.current.delete(row.id)
        }
      }
    },
    []
  )

  useEffect(() => {
    const gen = ++planMembersFetchGen.current
    planMembersInFlight.current.clear()
    for (const row of issues) {
      void loadPlanMembers(row, gen)
    }
  }, [issues, loadPlanMembers])

  const breadcrumbItems = useMemo(
    () => [
      { title: 'Home', path: '/' },
      {
        title: 'Benefit Integrations',
        path: benefitIntegrationsListPath(),
      },
      { title: 'Noyo', path: noyoIssuesPath() },
      { title: 'Issues' },
    ],
    []
  )

  const columns = useMemo<ColumnsType<NoyoDifference>>(
    () => [
      {
        title: 'Queue',
        key: 'queue',
        width: 140,
        render: (_: unknown, row: NoyoDifference) => (
          <Tag color={problemTagColor(row.issueQueue)}>
            {issueQueueLabel(row.issueQueue)}
          </Tag>
        ),
      },
      {
        title: 'SureCo member',
        key: 'surecoMember',
        render: (_: unknown, row: NoyoDifference) => {
          const planMembers = planMembersById[row.id]
          return (
            <MemberNameCell
              side="sureco"
              noyoEmployeeId={row.employee_id}
              fields={row.fields}
              fetchedMembers={planMembers?.surecoMembers}
              fetching={planMembers?.loading === true}
            />
          )
        },
      },
      {
        title: 'Noyo member',
        key: 'noyoMember',
        render: (_: unknown, row: NoyoDifference) => {
          const planMembers = planMembersById[row.id]
          return (
            <MemberNameCell
              side="noyo"
              noyoEmployeeId={row.employee_id}
              fields={row.fields}
              fetchedMembers={planMembers?.noyoMembers}
              fetching={planMembers?.loading === true}
            />
          )
        },
      },
      {
        title: 'SureCo plan',
        key: 'surecoPlan',
        render: (_: unknown, row: NoyoDifference) => {
          const planMembers = planMembersById[row.id]
          const mismatches = planDateMismatches(
            planMembers?.surecoPlan,
            planMembers?.noyoPlan
          )
          return (
            <SureCoPlanCell
              plan={planMembers?.surecoPlan}
              loading={planMembers?.loading === true}
              highlightStart={mismatches.surecoStartsBeforeNoyo}
              highlightEnd={mismatches.surecoEndsAfterNoyo}
            />
          )
        },
      },
      {
        title: 'Noyo plan',
        key: 'noyoPlan',
        render: (_: unknown, row: NoyoDifference) => {
          const planMembers = planMembersById[row.id]
          const mismatches = planDateMismatches(
            planMembers?.surecoPlan,
            planMembers?.noyoPlan
          )
          return (
            <NoyoPlanCell
              planId={planIdFromFields(row.fields)}
              plan={planMembers?.noyoPlan}
              loading={planMembers?.loading === true}
              highlightStart={mismatches.surecoStartsBeforeNoyo}
              highlightEnd={mismatches.surecoEndsAfterNoyo}
            />
          )
        },
      },
      {
        title: 'Type',
        dataIndex: 'difference_type',
        key: 'type',
        render: (value: string) => differenceTypeLabel(value),
      },
      {
        title: 'Impact',
        dataIndex: 'impact_score',
        key: 'impact',
        width: 120,
        render: (score: number) => (
          <Tag color={impactColor(score)}>
            {score} {impactLabel(score)}
          </Tag>
        ),
      },
      {
        title: 'Discrepancy time',
        dataIndex: 'discrepancy_time',
        key: 'discrepancyTime',
        render: (value?: number) => formatUnixTime(value),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_: unknown, row: NoyoDifference) => {
          const differenceId = row.id
          return (
            <AntButton
              disabled={differenceId.length === 0}
              onClick={() => {
                if (differenceId.length === 0) {
                  return
                }
                window.open(
                  noyoTrackingUrl(differenceId),
                  '_blank',
                  'noopener,noreferrer'
                )
              }}
            >
              Goto Noyo
            </AntButton>
          )
        },
      },
    ],
    [planMembersById]
  )

  return (
    <Page
      title="Noyo Issues"
      maxWidth="100%"
      breadcrumb={<BreadcrumbNav items={breadcrumbItems} />}
    >
      <Stack direction="col" space={16}>
        <Alert
          type="info"
          showIcon
          message="Queue is classified per row"
          description="Needs action is coverage or employment that still looks wrong. Policy / dates is mapping after coverage already ended. Noise is address casing, phones, and similar."
          role="status"
        />
        <Stack
          direction="row"
          space={12}
          style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Space wrap>
            <Select
              aria-label="Queue"
              mode="multiple"
              allowClear
              placeholder="Queue"
              value={selectedQueues}
              options={[...NOYO_ISSUE_QUEUE_OPTIONS]}
              onChange={(value: unknown) => {
                if (!Array.isArray(value)) {
                  return
                }
                setSelectedQueues(
                  value
                    .filter((item): item is string => typeof item === 'string')
                    .filter(isNoyoIssueQueue)
                )
              }}
              style={{ minWidth: 240 }}
            />
            <Select
              aria-label="Classification"
              mode="multiple"
              allowClear
              placeholder="Classification"
              value={classifications}
              options={[...NOYO_CLASSIFICATION_OPTIONS]}
              onChange={(value: unknown) => {
                if (!Array.isArray(value)) {
                  return
                }
                setClassifications(
                  value
                    .filter((item): item is string => typeof item === 'string')
                    .filter(isNoyoClassification)
                )
              }}
              style={{ minWidth: 280 }}
            />
          </Space>
          <Button
            icon={<ReloadOutlined aria-hidden="true" />}
            onClick={() => void refresh()}
          >
            Refresh
          </Button>
        </Stack>

        {!loading && loadError ? (
          <CenteredActionAlert>
            <Alert
              type="error"
              showIcon
              role="alert"
              message="Failed to load Noyo issues"
              description={loadError}
              action={
                <Button
                  icon={<ReloadOutlined aria-hidden="true" />}
                  onClick={() => void refresh()}
                >
                  Retry
                </Button>
              }
            />
          </CenteredActionAlert>
        ) : null}

        <TableWrap>
          <Table
            rowKey={(row: NoyoDifference) => row.id}
            loading={loading}
            dataSource={visibleIssues}
            columns={columns}
            locale={{ emptyText: 'No Noyo issues found' }}
            rowClassName={(row: NoyoDifference) => {
              const planMembers = planMembersById[row.id]
              if (
                surecoActiveWhileNoyoTerminated(
                  planMembers?.surecoMembers,
                  planMembers?.noyoMembers
                )
              ) {
                return 'noyo-issue-row-noyo-terminated'
              }
              return ''
            }}
            onRow={(row: NoyoDifference) => {
              const planMembers = planMembersById[row.id]
              if (
                surecoActiveWhileNoyoTerminated(
                  planMembers?.surecoMembers,
                  planMembers?.noyoMembers
                )
              ) {
                return {
                  title: 'SureCo is still active while Noyo is terminated',
                }
              }
              return {}
            }}
            expandable={{
              expandIcon: NoyoIssueExpandIcon,
              expandedRowRender: (row: NoyoDifference) => (
                <FieldsComparison fields={row.fields} />
              ),
              rowExpandable: (row: NoyoDifference) =>
                Boolean(row.fields && row.fields.length > 0),
            }}
            pagination={{
              current: page,
              pageSize,
              total: meta.total_records ?? 0,
              showSizeChanger: true,
              onChange: (nextPage: number, nextPageSize: number) => {
                if (nextPageSize !== pageSize) {
                  setPageSize(nextPageSize)
                  setPage(1)
                } else {
                  setPage(nextPage)
                }
              },
            }}
          />
        </TableWrap>
      </Stack>
    </Page>
  )
}
